import apis from "../../utils/apis";
import { showFeedbackModal } from "./UserFeedbackActions";
import {
  FINANCE_SET_ACCOUNT,
  FINANCE_SET_BALANCE,
  FINANCE_SET_TRANSACTIONS,
  FINANCE_SET_INVOICES,
  FINANCE_SET_PAYOUTS,
  FINANCE_SET_SUMMARY,
  FINANCE_SET_LOADING,
  FINANCE_SET_ERROR,
  FINANCE_SET_EXPENSES,
  FINANCE_SET_BILLING_RATES,
  FINANCE_SET_DISTRIBUTIONS,
  FINANCE_SET_SELECTED_INVOICE,
  FINANCE_SET_EXPENSE_SUMMARY,
  FINANCE_SET_UNBILLED_EXPENSES,
} from "../types";
import { handleApiError } from "./utils/utils";

// ── Helpers ─────────────────────────────────────────────────────

const setLoading = (key: string, value: boolean) => ({
  type: FINANCE_SET_LOADING,
  payload: { key, value },
});

const setError = (error: string | null) => ({
  type: FINANCE_SET_ERROR,
  payload: error,
});

// ── Account Actions ─────────────────────────────────────────────

export const fetchFinanceAccount = (organizationId: number) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("account", true));

      const resp = await apis({
        method: "GET",
        url: `/finance/account/details?organization_id=${organizationId}`,
      });

      if (resp && resp.status === 200) {
        dispatch({ type: FINANCE_SET_ACCOUNT, payload: resp.data });
      } else {
        handleApiError(resp, dispatch);
      }
    } catch (err: any) {
      // No account connected yet — not an error
      if (err?.response?.status === 400) {
        dispatch({ type: FINANCE_SET_ACCOUNT, payload: null });
      } else {
        handleApiError(err, dispatch);
      }
    } finally {
      dispatch(setLoading("account", false));
    }
  };
};

export const connectBankAccount = (data: {
  organization_id: number;
  email?: string;
  business_name?: string;
  country?: string;
  refresh_url: string;
  return_url: string;
}) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("connect", true));

      const resp = await apis({
        method: "POST",
        url: "/finance/account/connect",
        data,
      });

      if (resp && resp.status === 200) {
        dispatch({ type: FINANCE_SET_ACCOUNT, payload: resp.data.account });
        return resp.data; // Contains onboarding_url
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    } finally {
      dispatch(setLoading("connect", false));
    }
  };
};

export const fetchBalance = (organizationId: number) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("balance", true));

      const resp = await apis({
        method: "GET",
        url: `/finance/account/balance?organization_id=${organizationId}`,
      });

      if (resp && resp.status === 200) {
        dispatch({ type: FINANCE_SET_BALANCE, payload: resp.data });
      } else {
        handleApiError(resp, dispatch);
      }
    } catch (err: any) {
      // No connected account yet — not an error, just no balance
      if (err?.response?.status === 400) {
        dispatch({ type: FINANCE_SET_BALANCE, payload: null });
      } else {
        handleApiError(err, dispatch);
      }
    } finally {
      dispatch(setLoading("balance", false));
    }
  };
};

export const createStripeDashboardLink = (organizationId: number) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: "/finance/account/login-link",
        data: { organization_id: organizationId },
      });

      if (resp && resp.status === 200) {
        return resp.data; // { url }
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

// ── Transaction Actions ─────────────────────────────────────────

export const fetchTransactions = (
  organizationId: number,
  params?: { page?: number; per_page?: number; type?: string; status?: string; client_id?: number; matter_id?: number }
) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("transactions", true));

      const queryParams = new URLSearchParams();
      queryParams.set("organization_id", String(organizationId));
      if (params?.page) queryParams.set("page", String(params.page));
      if (params?.per_page) queryParams.set("per_page", String(params.per_page));
      if (params?.type) queryParams.set("type", params.type);
      if (params?.status) queryParams.set("status", params.status);
      if (params?.client_id) queryParams.set("client_id", String(params.client_id));
      if (params?.matter_id) queryParams.set("matter_id", String(params.matter_id));

      const resp = await apis({
        method: "GET",
        url: `/finance/transactions?${queryParams.toString()}`,
      });

      if (resp && resp.status === 200) {
        dispatch({ type: FINANCE_SET_TRANSACTIONS, payload: resp.data });
      } else {
        handleApiError(resp, dispatch);
      }
    } catch (err) {
      handleApiError(err, dispatch);
    } finally {
      dispatch(setLoading("transactions", false));
    }
  };
};

export const fetchFinancialSummary = (organizationId: number, days?: number) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("summary", true));

      const url = `/finance/transactions/summary?organization_id=${organizationId}${days ? `&days=${days}` : ""}`;
      const resp = await apis({ method: "GET", url });

      if (resp && resp.status === 200) {
        dispatch({ type: FINANCE_SET_SUMMARY, payload: resp.data });
      } else {
        handleApiError(resp, dispatch);
      }
    } catch (err: any) {
      // No connected account yet — not an error, just no summary
      if (err?.response?.status === 400) {
        dispatch({ type: FINANCE_SET_SUMMARY, payload: null });
      } else {
        handleApiError(err, dispatch);
      }
    } finally {
      dispatch(setLoading("summary", false));
    }
  };
};

export const syncTransactions = (organizationId: number) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("sync", true));

      const resp = await apis({
        method: "POST",
        url: "/finance/transactions/sync",
        data: { organization_id: organizationId },
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: `Synced ${resp.data.synced} transactions`,
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    } finally {
      dispatch(setLoading("sync", false));
    }
  };
};

// ── Invoice Actions ─────────────────────────────────────────────

export const fetchInvoices = (
  organizationId: number,
  params?: { page?: number; per_page?: number; status?: string; client_id?: number; matter_id?: number }
) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("invoices", true));

      const queryParams = new URLSearchParams();
      queryParams.set("organization_id", String(organizationId));
      if (params?.page) queryParams.set("page", String(params.page));
      if (params?.per_page) queryParams.set("per_page", String(params.per_page));
      if (params?.status) queryParams.set("status", params.status);
      if (params?.client_id) queryParams.set("client_id", String(params.client_id));
      if (params?.matter_id) queryParams.set("matter_id", String(params.matter_id));

      const resp = await apis({
        method: "GET",
        url: `/finance/invoices?${queryParams.toString()}`,
      });

      if (resp && resp.status === 200) {
        dispatch({ type: FINANCE_SET_INVOICES, payload: resp.data });
      } else {
        handleApiError(resp, dispatch);
      }
    } catch (err) {
      handleApiError(err, dispatch);
    } finally {
      dispatch(setLoading("invoices", false));
    }
  };
};

export const createInvoice = (data: {
  organization_id: number;
  client_id?: number;
  matter_id?: number;
  line_items: { description: string; amount: number; quantity: number }[];
  currency?: string;
  due_days?: number;
  notes?: string;
  tax_amount?: number;
  discount_amount?: number;
  user_id?: number;
  tax_rate?: number;
  discount_rate?: number;
  internal_memo?: string;
  billing_address?: Record<string, any>;
  payment_terms?: string;
  is_recurring?: boolean;
  recurring_interval?: string;
  expense_ids?: number[];
}) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("createInvoice", true));

      const resp = await apis({
        method: "POST",
        url: "/finance/invoices",
        data,
      });

      if (resp && resp.status === 201) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Invoice created successfully",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    } finally {
      dispatch(setLoading("createInvoice", false));
    }
  };
};

export const sendInvoice = (invoiceId: number, organizationId: number, clientEmail?: string) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("sendInvoice", true));

      const resp = await apis({
        method: "POST",
        url: `/finance/invoices/${invoiceId}/send`,
        data: { organization_id: organizationId, client_email: clientEmail },
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Invoice sent — payment link generated",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    } finally {
      dispatch(setLoading("sendInvoice", false));
    }
  };
};

export const voidInvoice = (invoiceId: number, organizationId: number) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: `/finance/invoices/${invoiceId}/void`,
        data: { organization_id: organizationId },
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Invoice voided",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

export const deleteInvoice = (invoiceId: number, organizationId: number) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "DELETE",
        url: `/finance/invoices/${invoiceId}?organization_id=${organizationId}`,
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Invoice deleted",
          modalType: "success",
          duration: 3000,
        }));
        return true;
      } else {
        handleApiError(resp, dispatch);
        return false;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return false;
    }
  };
};

export const checkPaymentStatus = (invoiceId: number, organizationId: number) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: `/finance/invoices/${invoiceId}/check-payment`,
        data: { organization_id: organizationId },
      });

      if (resp && resp.status === 200) {
        const updated = resp.data?.invoice;
        if (updated?.status === "paid") {
          dispatch(showFeedbackModal({
            showModal: true,
            message: "Invoice has been paid!",
            modalType: "success",
            duration: 3000,
          }));
        }
        return updated || null;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

// ── Payout Actions ──────────────────────────────────────────────

export const fetchPayouts = (
  organizationId: number,
  params?: { page?: number; per_page?: number; status?: string }
) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("payouts", true));

      const queryParams = new URLSearchParams();
      queryParams.set("organization_id", String(organizationId));
      if (params?.page) queryParams.set("page", String(params.page));
      if (params?.per_page) queryParams.set("per_page", String(params.per_page));
      if (params?.status) queryParams.set("status", params.status);

      const resp = await apis({
        method: "GET",
        url: `/finance/payouts?${queryParams.toString()}`,
      });

      if (resp && resp.status === 200) {
        dispatch({ type: FINANCE_SET_PAYOUTS, payload: resp.data });
      } else {
        handleApiError(resp, dispatch);
      }
    } catch (err) {
      handleApiError(err, dispatch);
    } finally {
      dispatch(setLoading("payouts", false));
    }
  };
};

export const triggerPayout = (data: {
  organization_id: number;
  amount: number;
  currency?: string;
  description?: string;
}) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("triggerPayout", true));

      const resp = await apis({
        method: "POST",
        url: "/finance/payouts/trigger",
        data,
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Payout initiated successfully",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    } finally {
      dispatch(setLoading("triggerPayout", false));
    }
  };
};

export const syncPayouts = (organizationId: number) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("syncPayouts", true));

      const resp = await apis({
        method: "POST",
        url: "/finance/payouts/sync",
        data: { organization_id: organizationId },
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: `Synced ${resp.data.synced} payouts`,
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    } finally {
      dispatch(setLoading("syncPayouts", false));
    }
  };
};

// ── Invoice Extended Actions ────────────────────────────────────

export const fetchInvoice = (invoiceId: number, organizationId: number) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("selectedInvoice", true));

      const resp = await apis({
        method: "GET",
        url: `/finance/invoices/${invoiceId}?organization_id=${organizationId}`,
      });

      if (resp && resp.status === 200) {
        dispatch({ type: FINANCE_SET_SELECTED_INVOICE, payload: resp.data });
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    } finally {
      dispatch(setLoading("selectedInvoice", false));
    }
  };
};

export const updateInvoice = (invoiceId: number, data: Record<string, any>) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("updateInvoice", true));

      const resp = await apis({
        method: "PUT",
        url: `/finance/invoices/${invoiceId}`,
        data,
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Invoice updated",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    } finally {
      dispatch(setLoading("updateInvoice", false));
    }
  };
};

export const duplicateInvoice = (invoiceId: number, organizationId: number) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("duplicateInvoice", true));

      const resp = await apis({
        method: "POST",
        url: `/finance/invoices/${invoiceId}/duplicate`,
        data: { organization_id: organizationId },
      });

      if (resp && resp.status === 201) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Invoice duplicated as new draft",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    } finally {
      dispatch(setLoading("duplicateInvoice", false));
    }
  };
};

// ── Expense Actions ─────────────────────────────────────────────

export const fetchExpenses = (
  organizationId: number,
  params?: {
    page?: number;
    per_page?: number;
    status?: string;
    category?: string;
    matter_id?: number;
    client_id?: number;
    user_id?: number;
    is_billable?: boolean;
  }
) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("expenses", true));

      const queryParams = new URLSearchParams();
      queryParams.set("organization_id", String(organizationId));
      if (params?.page) queryParams.set("page", String(params.page));
      if (params?.per_page) queryParams.set("per_page", String(params.per_page));
      if (params?.status) queryParams.set("status", params.status);
      if (params?.category) queryParams.set("category", params.category);
      if (params?.matter_id) queryParams.set("matter_id", String(params.matter_id));
      if (params?.client_id) queryParams.set("client_id", String(params.client_id));
      if (params?.user_id) queryParams.set("user_id", String(params.user_id));
      if (params?.is_billable !== undefined) queryParams.set("is_billable", String(params.is_billable));

      const resp = await apis({
        method: "GET",
        url: `/finance/expenses?${queryParams.toString()}`,
      });

      if (resp && resp.status === 200) {
        dispatch({ type: FINANCE_SET_EXPENSES, payload: resp.data });
      } else {
        handleApiError(resp, dispatch);
      }
    } catch (err) {
      handleApiError(err, dispatch);
    } finally {
      dispatch(setLoading("expenses", false));
    }
  };
};

export const createExpense = (data: {
  organization_id: number;
  user_id?: number;
  category: string;
  description?: string;
  amount: number;
  currency?: string;
  matter_id?: number;
  client_id?: number;
  receipt_url?: string;
  is_billable?: boolean;
  expense_date?: string;
}) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("createExpense", true));

      const resp = await apis({
        method: "POST",
        url: "/finance/expenses",
        data,
      });

      if (resp && resp.status === 201) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Expense recorded",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    } finally {
      dispatch(setLoading("createExpense", false));
    }
  };
};

export const updateExpense = (expenseId: number, data: Record<string, any>) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("updateExpense", true));

      const resp = await apis({
        method: "PUT",
        url: `/finance/expenses/${expenseId}`,
        data,
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Expense updated",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    } finally {
      dispatch(setLoading("updateExpense", false));
    }
  };
};

export const deleteExpense = (expenseId: number, organizationId: number) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "DELETE",
        url: `/finance/expenses/${expenseId}?organization_id=${organizationId}`,
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Expense deleted",
          modalType: "success",
          duration: 3000,
        }));
        return true;
      } else {
        handleApiError(resp, dispatch);
        return false;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return false;
    }
  };
};

export const approveExpense = (expenseId: number, organizationId: number, approvedByUserId?: number) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: `/finance/expenses/${expenseId}/approve`,
        data: { organization_id: organizationId, approved_by_user_id: approvedByUserId },
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Expense approved",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

export const writeOffExpense = (expenseId: number, organizationId: number) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: `/finance/expenses/${expenseId}/write-off`,
        data: { organization_id: organizationId },
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Expense written off",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

export const fetchUnbilledExpenses = (
  organizationId: number,
  clientId?: number,
  matterId?: number,
) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("unbilledExpenses", true));

      const queryParams = new URLSearchParams();
      queryParams.set("organization_id", String(organizationId));
      if (clientId) queryParams.set("client_id", String(clientId));
      if (matterId) queryParams.set("matter_id", String(matterId));

      const resp = await apis({
        method: "GET",
        url: `/finance/expenses/unbilled?${queryParams.toString()}`,
      });

      if (resp && resp.status === 200) {
        dispatch({ type: FINANCE_SET_UNBILLED_EXPENSES, payload: resp.data.expenses });
        return resp.data.expenses;
      } else {
        handleApiError(resp, dispatch);
        return [];
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return [];
    } finally {
      dispatch(setLoading("unbilledExpenses", false));
    }
  };
};

export const fetchExpenseSummary = (organizationId: number) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("expenseSummary", true));

      const resp = await apis({
        method: "GET",
        url: `/finance/expenses/summary?organization_id=${organizationId}`,
      });

      if (resp && resp.status === 200) {
        dispatch({ type: FINANCE_SET_EXPENSE_SUMMARY, payload: resp.data });
      } else {
        handleApiError(resp, dispatch);
      }
    } catch (err) {
      handleApiError(err, dispatch);
    } finally {
      dispatch(setLoading("expenseSummary", false));
    }
  };
};

// ── Billing Rate Actions ────────────────────────────────────────

export const fetchBillingRates = (
  organizationId: number,
  params?: {
    user_id?: number;
    client_id?: number;
    matter_id?: number;
    rate_type?: string;
    active_only?: boolean;
  }
) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("billingRates", true));

      const queryParams = new URLSearchParams();
      queryParams.set("organization_id", String(organizationId));
      if (params?.user_id) queryParams.set("user_id", String(params.user_id));
      if (params?.client_id) queryParams.set("client_id", String(params.client_id));
      if (params?.matter_id) queryParams.set("matter_id", String(params.matter_id));
      if (params?.rate_type) queryParams.set("rate_type", params.rate_type);
      if (params?.active_only !== undefined) queryParams.set("active_only", String(params.active_only));

      const resp = await apis({
        method: "GET",
        url: `/finance/billing-rates?${queryParams.toString()}`,
      });

      if (resp && resp.status === 200) {
        dispatch({ type: FINANCE_SET_BILLING_RATES, payload: resp.data.rates });
      } else {
        handleApiError(resp, dispatch);
      }
    } catch (err) {
      handleApiError(err, dispatch);
    } finally {
      dispatch(setLoading("billingRates", false));
    }
  };
};

export const createBillingRate = (data: {
  organization_id: number;
  rate_type: string;
  amount: number;
  currency?: string;
  user_id?: number;
  client_id?: number;
  matter_id?: number;
  description?: string;
  is_default?: boolean;
  effective_from?: string;
  effective_to?: string;
}) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("createBillingRate", true));

      const resp = await apis({
        method: "POST",
        url: "/finance/billing-rates",
        data,
      });

      if (resp && resp.status === 201) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Billing rate created",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    } finally {
      dispatch(setLoading("createBillingRate", false));
    }
  };
};

export const updateBillingRate = (rateId: number, data: Record<string, any>) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("updateBillingRate", true));

      const resp = await apis({
        method: "PUT",
        url: `/finance/billing-rates/${rateId}`,
        data,
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Billing rate updated",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    } finally {
      dispatch(setLoading("updateBillingRate", false));
    }
  };
};

export const deleteBillingRate = (rateId: number, organizationId: number) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "DELETE",
        url: `/finance/billing-rates/${rateId}?organization_id=${organizationId}`,
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Billing rate deleted",
          modalType: "success",
          duration: 3000,
        }));
        return true;
      } else {
        handleApiError(resp, dispatch);
        return false;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return false;
    }
  };
};

/**
 * Resolve the applicable billing rate for a given org/user/client/matter scope.
 * Returns the rate object or null. Does not dispatch to Redux — pure lookup.
 */
export const resolveBillingRate = async (params: {
  organization_id: number;
  user_id?: number;
  client_id?: number;
  matter_id?: number;
}): Promise<any | null> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.set("organization_id", String(params.organization_id));
    if (params.user_id) queryParams.set("user_id", String(params.user_id));
    if (params.client_id) queryParams.set("client_id", String(params.client_id));
    if (params.matter_id) queryParams.set("matter_id", String(params.matter_id));

    const resp = await apis({
      method: "GET",
      url: `/finance/billing-rates/resolve?${queryParams.toString()}`,
    });

    if (resp && resp.status === 200 && resp.data?.rate) {
      return resp.data.rate;
    }
    return null;
  } catch {
    return null;
  }
};

// ── Distribution Actions ────────────────────────────────────────

export const fetchDistributions = (
  organizationId: number,
  params?: {
    page?: number;
    per_page?: number;
    status?: string;
    user_id?: number;
    type?: string;
  }
) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("distributions", true));

      const queryParams = new URLSearchParams();
      queryParams.set("organization_id", String(organizationId));
      if (params?.page) queryParams.set("page", String(params.page));
      if (params?.per_page) queryParams.set("per_page", String(params.per_page));
      if (params?.status) queryParams.set("status", params.status);
      if (params?.user_id) queryParams.set("user_id", String(params.user_id));
      if (params?.type) queryParams.set("type", params.type);

      const resp = await apis({
        method: "GET",
        url: `/finance/distributions?${queryParams.toString()}`,
      });

      if (resp && resp.status === 200) {
        dispatch({ type: FINANCE_SET_DISTRIBUTIONS, payload: resp.data });
      } else {
        handleApiError(resp, dispatch);
      }
    } catch (err) {
      handleApiError(err, dispatch);
    } finally {
      dispatch(setLoading("distributions", false));
    }
  };
};

export const createDistribution = (data: {
  organization_id: number;
  user_id: number;
  amount: number;
  currency?: string;
  type?: string;
  payout_id?: number;
  matter_id?: number;
  invoice_id?: number;
  notes?: string;
}) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoading("createDistribution", true));

      const resp = await apis({
        method: "POST",
        url: "/finance/distributions",
        data,
      });

      if (resp && resp.status === 201) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Distribution created",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    } finally {
      dispatch(setLoading("createDistribution", false));
    }
  };
};

export const approveDistribution = (distId: number, organizationId: number, approvedByUserId?: number) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: `/finance/distributions/${distId}/approve`,
        data: { organization_id: organizationId, approved_by_user_id: approvedByUserId },
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Distribution approved",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

export const markDistributionPaid = (distId: number, organizationId: number) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: `/finance/distributions/${distId}/mark-paid`,
        data: { organization_id: organizationId },
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Distribution marked as paid",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

export const cancelDistribution = (distId: number, organizationId: number) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: `/finance/distributions/${distId}/cancel`,
        data: { organization_id: organizationId },
      });

      if (resp && resp.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: "Distribution cancelled",
          modalType: "success",
          duration: 3000,
        }));
        return resp.data;
      } else {
        handleApiError(resp, dispatch);
        return null;
      }
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};
